import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { format } from 'date-fns';
import { html } from 'lit';
import { getRoutePlanningSummaries, RoutePlanningSummary } from '../api/route-plannings-table.js';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return '—';
  const rounded = Math.round(minutes);
  const hours = Math.floor(rounded / 60);
  const mins = Math.abs(rounded % 60);
  if (hours > 0) return `${hours}h ${mins}min`;
  return `${mins}min`;
}

export const RoutePlanningsPage = stateful(() => {
  const dataviewOptions = buildFullOptions({
    class: RoutePlanningSummary,
    operation: getRoutePlanningSummaries,
    customColumns: [
      {
        fieldName: 'createdAt',
        columnName: 'Created At',
        isSortable: true,
        renderItem: entity => html`${entity.createdAt ? format(new Date(entity.createdAt), 'dd.MM.yyyy HH:mm') : '—'}`,
      },
      {
        fieldName: 'simulationType',
        columnName: 'Simulation Type',
        isSortable: true,
      },
      {
        fieldName: 'profitability',
        columnName: 'Profitability',
        isSortable: true,
        renderItem: entity => {
          const value = entity.profitability ?? 0;
          return html`<span style="color: ${value >= 0 ? '#4caf50' : '#e53935'}; font-weight: 600;"
            >${formatCurrency(value)}</span
          >`;
        },
      },
      {
        fieldName: 'longestWorkingDayMinutes',
        columnName: 'Longest Working Day',
        isSortable: true,
        renderItem: entity => {
          const minutes = entity.longestWorkingDayMinutes ?? 0;
          const exceedsTenHours = minutes > 10 * 60;
          return exceedsTenHours
            ? html`<span style="color: #e53935; font-weight: 700;">${formatMinutes(minutes)}</span>`
            : html`${formatMinutes(minutes)}`;
        },
      },
      {
        fieldName: 'internalFeUsage',
        columnName: 'Internal FE Usage',
        isSortable: true,
      },
      {
        fieldName: 'externalFeUsage',
        columnName: 'External FE Usage',
        isSortable: true,
      },
      {
        fieldName: 'totalOvertimeCost',
        columnName: 'Total Overtime Cost',
        isSortable: true,
        renderItem: entity => html`${formatCurrency(entity.totalOvertimeCost ?? 0)}`,
      },
    ],
    view: new View({
      filter: new AdornisFilter({
        sortingFields: ['createdAt'],
        sortingDirections: ['desc'],
      }),
    }),
  });

  return html`
    <div class="max-w-6xl mx-auto p-8">
      <div class="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h2 class="text-2xl font-bold">Route Plannings</h2>
          <p class="text-base text-neutral/60 mt-1">Overview of route planning runs with profitability and overtime</p>
        </div>
      </div>

      ${DataTable({ options: dataviewOptions.dataViewOptions })}
    </div>
  `;
});
